import NextAuth from "next-auth";

import Google from "next-auth/providers/google";
import GitHub from "next-auth/providers/github";
import Twitter from "next-auth/providers/twitter";
import CredentialsProvider from "next-auth/providers/credentials";
// import Discord from "next-auth/providers/discord";

import nacl from "tweetnacl";
import { PublicKey as SolPubKey } from "@solana/web3.js";
import { getBytes, hashMessage, recoverAddress } from "ethers";
import { IChain, AllChains } from "based-auth/chains";
import { saveSocialUser, saveWalletUser } from "./user-profile";
import { createKeypairs } from "./create-keypairs";
import { NetworkSymbols } from "based-auth/networks";

// Extend Twitter provider type
type TwitterProvider = typeof Twitter & {
  version?: "2.0";
};

if (!process.env.AUTH_GOOGLE_ID || !process.env.AUTH_GOOGLE_SECRET) {
  throw new Error("AUTH_GOOGLE_ID and AUTH_GOOGLE_SECRET must be set");
}
if (!process.env.AUTH_GITHUB_ID || !process.env.AUTH_GITHUB_SECRET) {
  throw new Error("AUTH_GITHUB_ID and AUTH_GITHUB_SECRET must be set");
}
if (!process.env.AUTH_TWITTER_ID || !process.env.AUTH_TWITTER_SECRET) {
  throw new Error("AUTH_TWITTER_ID and AUTH_TWITTER_SECRET must be set");
}

const authConfig = NextAuth({
  providers: [
    Twitter({
      version: "2.0",
      clientId: process.env.AUTH_TWITTER_ID,
      clientSecret: process.env.AUTH_TWITTER_SECRET,
      profile: async ({
        data: { profile_image_url, name, username },
      }: {
        data: {
          profile_image_url: string;
          name: string;
          username: string;
        };
      }) => {
        return await saveSocialUser(
          "twitter",
          username,
          name,
          profile_image_url
        );
      },
    } as any),
    Google({
      clientId: process.env.AUTH_GOOGLE_ID,
      clientSecret: process.env.AUTH_GOOGLE_SECRET,
      profile: async ({ name, email, picture }) => {
        return await saveSocialUser("google", email, name, picture);
      },
    }),
    GitHub({
      clientId: process.env.AUTH_GITHUB_ID,
      clientSecret: process.env.AUTH_GITHUB_SECRET,
      profile: async ({ name, login, avatar_url }) => {
        // html_url, email
        return await saveSocialUser("github", login, name || "", avatar_url);
      },
    }),

    CredentialsProvider({
      name: "Connect Wallet",
      credentials: {
        chainId: {
          label: "Chain Id",
          type: "number",
          placeholder: "1...",
        },
        address: {
          label: "Address",
          type: "text",
          placeholder: "0x0...",
        },
        message: {
          label: "Message",
          type: "text",
          placeholder: "hi...",
        },
        signature: {
          label: "Signature",
          type: "text",
          placeholder: "0x0...",
        },
      },
      async authorize(credentials: any) {
        if (
          !credentials.provider ||
          !credentials.chainId ||
          !credentials.address ||
          !credentials.message ||
          !credentials.signature
        ) {
          console.log("Missing credentials");
          return null;
        }
        const provider = credentials.provider;
        const chainId = Number(credentials.chainId);
        const address = credentials.address.toString();
        const message = credentials.message.toString();
        const signature = credentials.signature.toString();
        const chain = AllChains.find((c) => c.id === chainId);
        if (!chain) {
          console.log("Invalid chain");
          return null;
        }
        const network = chain.network.symbol;
        switch (network) {
          case "eth":
            const mesageHash = hashMessage(message);
            const recoveredAddress = recoverAddress(
              getBytes(mesageHash),
              signature
            );
            if (recoveredAddress.toLowerCase() !== address.toLowerCase()) {
              console.log("Invalid signature");
              return null;
            }
            break;
          case "sol":
            nacl.sign.detached.verify(
              new TextEncoder().encode(message),
              Buffer.from(signature, "base64"),
              new SolPubKey(address).toBytes()
            );
            break;
          default:
            throw new Error("Invalid network");
        }
        return await saveWalletUser(provider, chainId, network, address);
      },
    }),
  ],
  theme: {
    colorScheme: "dark",
  },
  callbacks: {
    async signIn() {
      return true;
    },
    async jwt({ token, user, trigger, session, ...rest }) {
      if (trigger === "update" && session.id === "switchChain") {
        if (!token.social) {
          throw new Error("Social not found");
        }
        token.chainId = session.chainId;
        const chain = AllChains.find((c) => c.id === session.chainId) as IChain;
        if (!chain) {
          throw new Error("Invalid chain");
        }
        token.network = chain.network.symbol;
        const { address } = createKeypairs(
          token.network as NetworkSymbols,
          token.social as string,
          token.provider as string
        );
        token.address = address;
      } else if (trigger === "update" && session.id === "switchAccount") {
        const selectedUser = (token as any).users.find(
          (u: { network: string; address: string }) =>
            u.network === session.network && u.address === session.address
        );
        if (selectedUser) {
          token.network = selectedUser.network;
          token.address = selectedUser.address;
          token.social = selectedUser.social;
          token.name = selectedUser.name;
          token.image = selectedUser.image;
          token.role = selectedUser.role;
          token.provider = selectedUser.provider;
        }
      }
      if (user) {
        return { ...token, ...user };
      }
      return token;
    },
    async session({ session, token }) {
      session.user = {
        id: token.id ? String(token._id) : "",
        name: typeof token.name === "string" ? token.name : undefined,
        chainId: typeof token.chainId === "number" ? token.chainId : undefined,
        network: typeof token.network === "string" ? token.network : undefined,
        address: typeof token.address === "string" ? token.address : undefined,
        provider:
          typeof token.provider === "string" ? token.provider : undefined,
        image: typeof token.image === "string" ? token.image : undefined,
        social: typeof token.social === "string" ? token.social : undefined,
        role: typeof token.role === "string" ? token.role : undefined,
        users: Array.isArray(token.users) ? token.users : undefined,
      } as any;
      return session;
    },
  },
  session: { strategy: "jwt", maxAge: 86400 },
  secret: process.env.AUTH_SECRET!,
});

export const { handlers, signIn, signOut } = authConfig;

export const auth: any = authConfig.auth;

export default authConfig;
